import { useEffect, useMemo, useRef, useState } from "react";

/**
 * Client-side infinite scroll ("windowing").
 *
 * Renders only the first `pageSize` items of `items`, then appends another
 * `pageSize` each time the returned `sentinelRef` element scrolls near the
 * viewport. The full list still lives in memory (so existing client-side
 * sort / search / totals / select-all keep working unchanged) — this only
 * bounds how many are mounted into the DOM at once, which is what actually
 * costs render time and memory on a large grid.
 *
 * Resets to the first page whenever `items` identity changes (new sort, new
 * search, refetch) so a filter never leaves the user scrolled into emptiness.
 * Pass a memoized array so identity is stable across unrelated re-renders.
 *
 * Usage:
 *   const { visible, hasMore, sentinelRef } = useWindowedList(sortedFiltered);
 *   ...
 *   {visible.map(renderCard)}
 *   {hasMore && <div ref={sentinelRef} aria-hidden />}
 */
export function useWindowedList<T>(items: T[], pageSize = 48) {
  const [count, setCount] = useState(pageSize);
  const sentinelRef = useRef<HTMLDivElement | null>(null);

  // New source list (sort/search/refetch) → back to the first page.
  useEffect(() => {
    setCount(pageSize);
  }, [items, pageSize]);

  const visible = useMemo(() => items.slice(0, count), [items, count]);
  const hasMore = count < items.length;

  useEffect(() => {
    if (!hasMore) return;
    const el = sentinelRef.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          setCount((c) => Math.min(c + pageSize, items.length));
        }
      },
      // Preload the next page before the sentinel is actually on screen so the
      // scroll feels continuous rather than hitching at the bottom.
      { rootMargin: "600px" },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [hasMore, items.length, pageSize]);

  return { visible, hasMore, sentinelRef };
}
